import { type ComponentType, createContext, type ReactNode, useContext } from 'react';
import type { CitationFocus } from '@/components/case-partner/DocumentViewerContent';

export interface SidePanelTab {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  content: ReactNode;
  disabled?: boolean;
}

export interface ShowCasePartnerTabOptions {
  activate?: boolean;
  open?: boolean;
}

export interface SidePanelContextType {
  isOpen: boolean;
  tabs: SidePanelTab[];
  activeTab: string;
  panelWidth: number;
  activeDocumentGuid: string | null;
  openPanel: (tabs: SidePanelTab[], activeTabId?: string) => void;
  openDocumentPanel: (params: {
    documentGuid: string;
    matterGuid?: string;
    citationNumber?: string;
    chunkId?: number;
    segmentIndex?: number;
    cellRange?: string;
    pageNumber?: number;
    activeTab?: 'overview' | 'document';
    attachmentGuid?: string;
    focusCitation?: CitationFocus;
  }) => void;
  openCasePartnerPanel: () => void;
  showCasePartnerTab: (options?: ShowCasePartnerTabOptions) => void;
  removeCasePartnerTab: () => void;
  closePanel: () => void;
  setActiveTab: (tabId: string) => void;
  setTabs: (tabs: SidePanelTab[]) => void;
  setPanelWidth: (width: number) => void;
  setActiveDocumentGuid: (guid: string | null) => void;
}

export const SidePanelContext = createContext<SidePanelContextType | undefined>(undefined);

export const useSidePanel = () => {
  const context = useContext(SidePanelContext);
  if (!context) {
    throw new Error('useSidePanel must be used within SidePanelProvider');
  }
  return context;
};
